import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export default function RegisterPage() {
  const { isAuthenticated, register, loginWithGoogle, loginWithApple, isFirebaseConfigured } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  if (isAuthenticated) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await register({ name, email, password });
    setLoading(false);
    if (!result.ok) { setError(result.message); return; }
    navigate('/', { replace: true });
  };

  const handleGoogleLogin = async () => {
    setError('');
    const result = await loginWithGoogle();
    if (!result.ok) { setError(result.message); return; }
    navigate('/', { replace: true });
  };

  const handleAppleLogin = async () => {
    setError('');
    const result = await loginWithApple();
    if (!result.ok) { setError(result.message); return; }
    navigate('/', { replace: true });
  };

  return (
    <div className="form-shell">
      <div className="form-card">
        <div className="mb-6 h-1.5 w-16 rounded-full bg-gradient-to-r from-[var(--brand-accent)] to-[var(--brand-primary)]" />

        <span className="pill">Get started</span>

        <h1 className="page-title section-gap">Create your account</h1>
        <p className="page-subtitle">Join {'KinetoCut'} in a few seconds.</p>

        <form className="section-gap" onSubmit={handleSubmit}>
          <div className="form-row">
            <label htmlFor="name">Full name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              autoComplete="name"
              placeholder="Jane Doe"
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="email">Email address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              autoComplete="email"
              placeholder="you@example.com"
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              autoComplete="new-password"
              placeholder="Min. 6 characters"
              required
            />
          </div>

          {error && (
            <div className="error-box">
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {error}
            </div>
          )}

          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <div className="my-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Or continue with</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <div className="social-stack">
          <button type="button" className="social-btn" onClick={handleGoogleLogin} disabled={!isFirebaseConfigured}>
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
          <button type="button" className="social-btn" onClick={handleAppleLogin} disabled={!isFirebaseConfigured}>
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16.365 1.43c0 1.14-.463 2.083-1.315 2.914-.943.916-2.054 1.437-3.15 1.35a3.31 3.31 0 0 1-.023-.394c0-1.088.477-2.088 1.34-2.913C13.99.68 15.152.126 16.09.06c.05.14.275.594.275 1.37zm3.973 16.42c-.312.72-.69 1.416-1.13 2.084-.6.913-1.09 1.545-1.466 1.895-.585.577-1.213.873-1.884.888-.483 0-1.066-.138-1.75-.417-.685-.278-1.315-.416-1.89-.416-.606 0-1.253.138-1.94.416-.687.279-1.242.425-1.665.44-.643.028-1.286-.276-1.93-.911-.407-.375-.92-1.03-1.535-1.965-.66-.995-1.203-2.148-1.628-3.463C1.09 15.008.87 13.674.87 12.383c0-1.474.32-2.75.958-3.822.5-.86 1.166-1.539 2.003-2.037a5.36 5.36 0 0 1 2.708-.766c.514 0 1.184.16 2.014.472.828.313 1.36.472 1.593.472.174 0 .758-.183 1.752-.55.94-.34 1.734-.481 2.383-.427 1.762.142 3.087.837 3.965 2.088-1.577.955-2.357 2.292-2.34 4.008.014 1.336.49 2.448 1.427 3.331.425.404.9.716 1.427.94-.115.332-.236.65-.365.955z"/>
            </svg>
            Continue with Apple
          </button>
          {!isFirebaseConfigured && (
            <p className="social-note">Enable Firebase configuration to use Google or Apple login.</p>
          )}
        </div>

        <p className="muted-link-row">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
